import {
    ORG_CHART_TREE_LOADING,
    ORG_CHART_TREE_LOADED,
    ORG_CHART_USERS_LOADED,
    ORG_CHART_ROLES_LOADED,
    ORG_CHART_MORE_USERS_LOADED,
    ORG_CHART_CHILDREN_LOADED,
    ORG_CHART_CHANGE_SELECTED_NODE,
    ORG_CHART_CHANGE_SELECTED_DATE,
    ORG_CHART_FILTERS_CHANGED,
    ORG_CHART_SORT_COLUMN_CHANGED
} from '../store/actionTypes';
import {createSelector} from 'reselect';
import {LOADING_STATUS} from "utils/constants";

import * as _ from 'lodash';
import type {DataGridRow, DataGridTasks} from "types/appDataGrid";
import type {OrgChartAction, OrgChartRoleOption, OrgChartState, OrgChartUser, OrgChartUsers} from "types/organisationChart";

let TODAY = new Date().toISOString().substring(0, 10);

export const DEFAULT_STATE: OrgChartState = {
    orgChartTree: null,
    users: {
        rows: []
    },
    usersLoadingStatus: null,
    selectedNode: null,
    roles: null,
    childrenObject: {},
    filters: {
        columns: {},
        date: TODAY,
        sort: null
    },
    sortingLogic: {}
};

export const getOrgChartUsersForRender = createSelector(
    [getOrgChartUsers],
    (users: OrgChartUsers): DataGridTasks => {
        let mappedUsers = _.cloneDeep(users) as DataGridTasks;

        let response = users.rows ? users.rows.map((user: OrgChartUser, index: number) => {
            let mappedUser = _.cloneDeep(user) as DataGridRow;
            mappedUser.isSelected = user.deputy;
            mappedUser.index = index;

            return mappedUser;
        }) : [];

        mappedUsers.rows = response;

        return mappedUsers;
    }
);

export default function organisationChartReducer(state: OrgChartState = DEFAULT_STATE, action: OrgChartAction): OrgChartState {
    switch (action.type) {

        case ORG_CHART_TREE_LOADING:
            return {...state, loadingStatus: LOADING_STATUS.LOADING};

        case ORG_CHART_TREE_LOADED:
            return {
                ...state,
                orgChartTree: action.data,
                filters: {...DEFAULT_STATE.filters, date: action.date || TODAY},
                users: {...DEFAULT_STATE.users},
                childrenObject: {},
                selectedNode: null,
                sortingLogic: {}
            };

        case ORG_CHART_CHILDREN_LOADED:
            return {...state, childrenObject: {...state.childrenObject, [String(action.nodeId)]: action.data}};

        case ORG_CHART_USERS_LOADED:
            return {...state, users: action.data, loadingStatus: LOADING_STATUS.DONE};

        case ORG_CHART_ROLES_LOADED:
            return {...state, roles: action.data};

        case ORG_CHART_MORE_USERS_LOADED: {
            const oldArray = state.users?.rows || [];
            return {...state, users: {...action.data, rows: oldArray.concat(action.data.rows)}};
        }

        case ORG_CHART_CHANGE_SELECTED_NODE:
            return {
                ...state,
                selectedNode: action.data,
                filters: {...DEFAULT_STATE.filters, date: state.filters.date || TODAY},
                sortingLogic: {}
            };

        case ORG_CHART_CHANGE_SELECTED_DATE:
            return {
                ...state,
                filters: {...state.filters, date: action.data, columns: {}, sort: null},
                selectedNode: null,
                sortingLogic: {}
            };

        case ORG_CHART_FILTERS_CHANGED:
            return {...state, filters: action.data};

        case ORG_CHART_SORT_COLUMN_CHANGED:
            return {
                ...state,
                filters: {...state.filters, sort: action.sortColumn ?? null},
                sortingLogic: {...action.sortLogic}
            };

        default:
            return state;
    }
}


export function getOrgChartUsers(state: OrgChartState): OrgChartUsers {
    return state.users;
}

export function getOrgChartSelectedNode(state: OrgChartState): OrgChartState['selectedNode'] {
    return state.selectedNode;
}

export function getOrgChartSelectedDate(state: OrgChartState): string {
    return state.filters.date;
}

export function getOrgChartRoles(state: OrgChartState): OrgChartRoleOption[] {
    let mappedRoles: OrgChartRoleOption[] = [];
    if (state.roles) {
        mappedRoles = state.roles.map(role => {
            return {actorTypeCode: role.orgChartRoleId, actorTypeName: role.name}
        });
    }
    return mappedRoles;
}

export function getOrgChartFilters(state: OrgChartState): OrgChartState['filters'] {
    return state.filters;
}

export function getOrgChartTree(state: OrgChartState): OrgChartState['orgChartTree'] {
    return state.orgChartTree;
}

export function getOrgChartSortingLogic(state: OrgChartState): OrgChartState['sortingLogic'] {
    return state.sortingLogic;
}

export function getOrgChartSortingColumn(state: OrgChartState): OrgChartState['filters']['sort'] {
    return state.filters.sort;
}

export function getOrgChartUsersLoadingStatus(state: OrgChartState): number | undefined {
    return state.loadingStatus;
}

export function getOrgChartChildNodes(state: OrgChartState): OrgChartState['childrenObject'] {
    return state.childrenObject;
}